import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildOpenEmsEvFleetUrl,
  fetchOpenEmsEvPortsFleetPower,
  fetchOpenEmsActivePortsGreenEnergyPct,
} from '../../utils/openEmsEvPortBattery';
import { HOST, PORT } from '../../environment';
import styles from './OpenEmsListEvPowerBanner.module.css';

const POLL_MS = 15_000;

/** Alumni Sans can draw '%' as wrong glyph; force a stack with a reliable U+0025. */
const FONT_STACK_PERCENT_SAFE =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';

function formatLocale(language) {
  const base = typeof language === 'string' ? language.split('-')[0] : '';
  if (base === 'en') return 'en-GB';
  if (base === 'ru') return 'ru-UA';
  return 'uk-UA';
}

function formatPowerKw(powerW, locale) {
  if (powerW == null || !Number.isFinite(powerW)) return '—';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(powerW / 1000);
}

export default function OpenEmsListEvPowerBanner({ className = '' }) {
  const { t, i18n } = useTranslation();
  const locale = useMemo(
    () => formatLocale(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage]
  );
  const [fleet, setFleet] = useState({ loading: true, powerW: null, activeSessions: 0 });
  const [greenPct, setGreenPct] = useState({ loading: true, value: null });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const apiOrigin = `${HOST}:${PORT}`;
      const fleetData = await fetchOpenEmsEvPortsFleetPower();
      const greenValue = await fetchOpenEmsActivePortsGreenEnergyPct(apiOrigin, fleetData?.powerW);
      if (cancelled) return;
      setFleet({
        loading: false,
        powerW: fleetData?.powerW ?? null,
        activeSessions: fleetData?.activeSessions ?? 0,
      });
      setGreenPct({ loading: false, value: greenValue });
    };

    setFleet(prev => ({ ...prev, loading: true }));
    setGreenPct(prev => ({ ...prev, loading: true }));
    void load();
    const id = window.setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const showBanner = fleet.loading || greenPct.loading || fleet.powerW != null || greenPct.value != null;
  if (!showBanner) return null;

  const pctLabel = greenPct.loading && greenPct.value == null ? '…' : String(greenPct.value ?? '—');
  const powerLabel = fleet.loading && fleet.powerW == null ? '…' : formatPowerKw(fleet.powerW, locale);
  const isGreen = greenPct.value != null && greenPct.value >= 70;

  return (
    <a
      className={[styles.banner, isGreen ? styles.bannerGreen : '', className].filter(Boolean).join(' ')}
      href={buildOpenEmsEvFleetUrl()}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('listOpenEmsBannerAria')}
    >
      {greenPct.value != null || greenPct.loading ? (
        <>
          <span className={styles.pctRow}>
            <span className={styles.pct} style={{ fontFamily: FONT_STACK_PERCENT_SAFE }}>
              {pctLabel}%
            </span>
          </span>
          <span className={styles.label}>{t('renewable')}</span>
        </>
      ) : null}

      <span className={styles.power}>
        {powerLabel}
        <span className={styles.powerUnit}> {t('powerWt')}</span>
      </span>

      {!fleet.loading && (
        <span className={styles.sessions}>{t('listOpenEmsEvPowerSessions', { count: fleet.activeSessions })}</span>
      )}
    </a>
  );
}
